/**
 * weapon_cycle.ts — the scroll-wheel weapon ring (combat wave, 2026-09-05).
 *
 * The ring is [bare hands, …every weapon the player has] ordered by tier,
 * then key, then quality (best first) — the same order the combat HUD's slot
 * row shows, so the wheel walks the row the player is looking at. The weapon
 * in hand is one entry of the ring (it lives in player_equipment, not the
 * bag), so a step from it lands on the next bag weapon; from bare hands it
 * lands on the first weapon. Pure: the harness pins the order and wrap-around.
 */
import { WEAPONS } from './weapon_catalog.js';

export interface RingWeapon { itemId: bigint; key: string; quality: number }

/** Bare hands as a ring entry (itemId 0n — the swap reducer's "unequip"). */
export const BARE_HANDS: RingWeapon = { itemId: 0n, key: '', quality: 0 };

function order(a: RingWeapon, b: RingWeapon): number {
  if (a.key === '' || b.key === '') return a.key === '' ? -1 : 1;
  const ta = WEAPONS[a.key]?.tier ?? 0, tb = WEAPONS[b.key]?.tier ?? 0;
  if (ta !== tb) return ta - tb;
  if (a.key !== b.key) return a.key < b.key ? -1 : 1;
  if (a.quality !== b.quality) return b.quality - a.quality;
  return a.itemId === b.itemId ? 0 : a.itemId < b.itemId ? -1 : 1;
}

/** The ring: bare hands first, then the equipped weapon and the bag's
 *  weapons in HUD order. `equipped` (itemId 0n, its key + quality) is null
 *  for bare hands. Non-weapons in `bag` are ignored. */
export function weaponRing(equipped: { key: string; quality: number } | null, bag: ReadonlyArray<RingWeapon>): RingWeapon[] {
  const ring: RingWeapon[] = [];
  if (equipped && equipped.key !== '' && WEAPONS[equipped.key]) ring.push({ itemId: 0n, key: equipped.key, quality: equipped.quality });
  for (const w of bag) if (w.key !== '' && WEAPONS[w.key]) ring.push(w);
  ring.sort(order);
  return [BARE_HANDS, ...ring];
}

/** The ring entry to swap to after one wheel step (+1 = next, −1 = previous),
 *  or null when there is nothing to cycle to (bare hands only). The equipped
 *  weapon is the entry with itemId 0n and a key; bare hands is the entry with
 *  itemId 0n and no key. */
export function nextWeapon(equipped: { key: string; quality: number } | null, bag: ReadonlyArray<RingWeapon>, dir: 1 | -1): RingWeapon | null {
  const ring = weaponRing(equipped, bag);
  if (ring.length < 2) return null;
  const cur = equipped && equipped.key !== '' ? ring.findIndex((r) => r.itemId === 0n && r.key !== '') : 0;
  const i = (Math.max(0, cur) + dir + ring.length) % ring.length;
  return ring[i];
}
